import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";

// Pages
import Landing from "@/pages/Landing";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import Dashboard from "@/pages/Dashboard";
import Nodes from "@/pages/Nodes";
import Sandbox from "@/pages/Sandbox";
import Proofs from "@/pages/Proofs";
import Credits from "@/pages/Credits";
import Cerebro from "@/pages/Cerebro";
import Marketplace from "@/pages/Marketplace";
import Settings from "@/pages/Settings";
import Superbrain from "@/pages/Superbrain";
import RoboMesh from "@/pages/RoboMesh";

// Layout
import AppLayout from "@/components/layout/AppLayout";

// Admin Panel
import { AdminAuthProvider } from "@/admin/utils/authContext";
import { AdminI18nProvider } from "@/admin/utils/i18nContext";
import ProtectedAdminRoute from "@/admin/components/ProtectedAdminRoute";
import AdminSetup from "@/admin/pages/AdminSetup";
import AdminLogin from "@/admin/pages/AdminLogin";
import AdminDashboard from "@/admin/pages/Dashboard";
import AdminUsers from "@/admin/pages/Users";
import AdminAnalytics from "@/admin/pages/Analytics";
import AdminExport from "@/admin/pages/ExportCenter";
import AdminHealth from "@/admin/pages/SystemHealth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      fetch(`${API}/auth/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => {
          if (!res.ok) throw new Error("Auth failed");
          return res.json();
        })
        .then(user => {
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { state: { user }, replace: true });
        })
        .catch(err => {
          console.error("Auth error:", err);
          toast.error("Authentication failed");
          navigate("/", { replace: true });
        });
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400 font-mono text-sm">Authenticating...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include"
        });
        
        if (!response.ok) throw new Error("Not authenticated");
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const DemoRoute = ({ children }) => {
  const demoUser = {
    user_id: "demo",
    email: "demo@neuromesh.io",
    name: "Demo User",
    role: "developer"
  };

  return (
    <AuthContext.Provider value={{ user: demoUser, setUser: () => {} }}>
      {children}
    </AuthContext.Provider>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isDemo = new URLSearchParams(location.search).get("demo") === "true";
  
  const ProtectedWrapper = isDemo ? DemoRoute : ProtectedRoute;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth-callback" element={<AuthCallbackPage />} />
      <Route path="/dashboard" element={
        <ProtectedWrapper>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/nodes" element={
        <ProtectedWrapper>
          <AppLayout>
            <Nodes />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/agents" element={
        <ProtectedWrapper>
          <AppLayout>
            <Sandbox />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/superbrain" element={
        <ProtectedWrapper>
          <AppLayout>
            <Superbrain />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/proofs" element={
        <ProtectedWrapper>
          <AppLayout>
            <Proofs />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/credits" element={
        <ProtectedWrapper>
          <AppLayout>
            <Credits />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/cerebro" element={
        <ProtectedWrapper>
          <AppLayout>
            <Cerebro />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/marketplace" element={
        <ProtectedWrapper>
          <AppLayout>
            <Marketplace />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/robomesh" element={
        <ProtectedWrapper>
          <AppLayout>
            <RoboMesh />
          </AppLayout>
        </ProtectedWrapper>
      } />
      <Route path="/settings" element={
        <ProtectedWrapper>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedWrapper>
      } />
      
      {/* Admin Panel Routes - Hidden */}
      <Route path="/neuro-admin-access/setup" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <AdminSetup />
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/login" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <AdminLogin />
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/dashboard" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/users" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/analytics" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <ProtectedAdminRoute><AdminAnalytics /></ProtectedAdminRoute>
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/export" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <ProtectedAdminRoute><AdminExport /></ProtectedAdminRoute>
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access/health" element={
        <AdminAuthProvider>
          <AdminI18nProvider>
            <ProtectedAdminRoute><AdminHealth /></ProtectedAdminRoute>
          </AdminI18nProvider>
        </AdminAuthProvider>
      } />
      <Route path="/neuro-admin-access" element={<Navigate to="/neuro-admin-access/setup" replace />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#030303]">
      <BrowserRouter>
        <LanguageProvider>
          <AppRouter />
        </LanguageProvider>
      </BrowserRouter>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#EDEDED'
          }
        }}
      />
    </div>
  );
}

export default App;
